import {
	faRightToBracket,
	faUserPlus,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import { Navigate, useNavigate } from 'react-router-dom';
import { useDisplaySettings } from '../hooks/useDisplaySettings';
import { usePortalAuth } from '../hooks/usePortalAuth';
import { useLoginModalStore } from '../stores/loginModalStore';
import { usePortalSiteStore } from '../stores/portalSiteStore';

/**
 * Home (`/`) for the storefront.
 *
 * Routing rules:
 *   - Authenticated + storefront prefers `defaultAuthenticatedView=
 *     'dashboard'` → /dashboard.
 *   - Authenticated + default view `'browse'` → /catalog.
 *   - Anonymous + anonymous browsing enabled → /dashboard. The dashboard
 *     is the brand-promoted home (carousel, category tiles, popular
 *     items, promo grid) and works for anon visitors — auth-aware widgets
 *     (Last Order, Reorder) self-hide on `token == null`, and the
 *     Account widget swaps in a sign-in CTA in the same slot. Sending
 *     anon visitors to /catalog instead skipped all of that promotional
 *     surface, which is the storefront's whole purpose.
 *   - Anonymous + anonymous browsing disabled → Sign In dead-end below.
 */
export function HomePage(): JSX.Element {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { isAuthenticated } = usePortalAuth();
	const display = useDisplaySettings();
	const openLoginModal = useLoginModalStore((s) => s.open);
	// Self-serve registration is per-portal-site (off by default). When the
	// active brand has opted in, the splash offers a "Create an account" CTA
	// next to Sign In so prospective B2B customers aren't dead-ended on a
	// sign-in-only screen. Coerced to a real boolean for the same tinyint
	// reason documented in LoginModal.
	const isRegistrationEnabled = usePortalSiteStore((s) =>
		Boolean(s.site?.isRegistrationEnabled),
	);
	const loginBackgroundUrl = usePortalSiteStore(
		(s) => s.site?.loginBackgroundUrl ?? null,
	);
	const brandLogoUrl = usePortalSiteStore((s) => s.site?.logoUrl ?? null);
	const brandLogoUrlLight = usePortalSiteStore(
		(s) => s.site?.logoUrlLight ?? null,
	);
	const loginTheme = usePortalSiteStore((s) => s.site?.loginTheme ?? 'dark');
	const brandDisplayName = usePortalSiteStore(
		(s) => s.site?.displayName ?? null,
	);

	// Routing rules per the staging redesign:
	//   1. Authenticated users land on Dashboard or Catalog depending on
	//      the storefront's `defaultAuthenticatedView` setting.
	//   2. Anonymous + anonymous browsing enabled → /dashboard. The
	//      dashboard is the brand storefront — promo carousel, category
	//      tiles, popular items, promo grid — and is the right surface
	//      for window-shopping. Account-aware widgets (Last Order,
	//      Reorder) self-hide on token == null; the Account widget swaps
	//      in a sign-in CTA in the same slot. Routing anon visitors to
	//      /catalog instead skipped all of the brand promotion, which
	//      defeats the point of the dashboard.
	//   3. Otherwise (locked-out anonymous), fall through to the
	//      branded welcome state below.
	if (isAuthenticated) {
		// The API has already capability-validated defaultAuthenticatedView
		// (resolver `coerceAuthenticatedView`), so the value is always one the
		// storefront can serve — no need to re-check feature flags here.
		switch (display.defaultAuthenticatedView) {
			case 'browse':
				return <Navigate to="/catalog" replace />;
			case 'orders':
				return <Navigate to="/orders" replace />;
			case 'account':
				return <Navigate to="/account" replace />;
			case 'dashboard':
			default:
				return <Navigate to="/dashboard" replace />;
		}
	}

	if (display.isAnonymousVisitor) {
		return <Navigate to="/dashboard" replace />;
	}

	// Locked-out anonymous fallback — branded welcome state with the sign-in
	// CTA. The portal's `loginTheme` (default 'dark') controls whether this
	// pre-login surface renders dark or light; an optional loginBackgroundUrl
	// fills it with a brand photo behind a theme-matched scrim.
	const hasBackground = loginBackgroundUrl != null && loginBackgroundUrl !== '';
	const isLight = loginTheme === 'light';
	// In light mode prefer the light-surface logo variant; fall back to the
	// default logo when a brand hasn't uploaded a separate light one.
	const effectiveLogoUrl = isLight
		? (brandLogoUrlLight ?? brandLogoUrl)
		: brandLogoUrl;
	// Photo scrim: dark in dark mode (keeps white text legible), light in
	// light mode (keeps dark text legible), centered on the text column.
	const scrimGradient = isLight
		? 'radial-gradient(ellipse 85% 55% at 50% 46%, rgba(255,255,255,0.94) 0%, rgba(255,255,255,0.8) 34%, rgba(255,255,255,0.42) 64%, rgba(255,255,255,0) 88%)'
		: 'radial-gradient(ellipse 80% 50% at 50% 48%, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.6) 30%, rgba(0,0,0,0.25) 60%, rgba(0,0,0,0) 85%)';
	return (
		<div
			className={`relative min-h-dvh flex items-center justify-center overflow-hidden ${
				isLight ? 'bg-white' : 'bg-brand-login-bg'
			}`}
		>
			{hasBackground ? (
				<>
					<img
						src={loginBackgroundUrl ?? ''}
						alt=""
						aria-hidden="true"
						className="absolute inset-0 h-full w-full object-cover"
					/>
					<div
						aria-hidden="true"
						className="absolute inset-0"
						style={{ background: scrimGradient }}
					/>
				</>
			) : null}

			<div
				className={`relative z-10 mx-6 max-w-md w-full text-center ${
					isLight ? 'text-[#0F1A2A]' : 'text-white'
				}`}
			>
				{effectiveLogoUrl != null && effectiveLogoUrl !== '' && (
					<div className="mb-8 flex justify-center">
						<img
							src={effectiveLogoUrl}
							alt={brandDisplayName ?? t('Brand logo')}
							// Bounded by both height and width so the bigger size
							// never overflows for wide (horizontal) wordmarks.
							className="max-h-28 sm:max-h-36 max-w-[80%] w-auto object-contain"
							style={{
								filter: isLight
									? 'drop-shadow(0 1px 3px rgba(0,0,0,0.12))'
									: 'drop-shadow(0 4px 12px rgba(0,0,0,0.5)) drop-shadow(0 1px 3px rgba(0,0,0,0.4))',
							}}
						/>
					</div>
				)}
				<h1
					className={`font-display text-4xl sm:text-5xl font-bold tracking-tight ${
						isLight ? 'text-[#0F1A2A]' : 'text-white'
					}`}
					style={
						isLight
							? undefined
							: {
									textShadow:
										'0 2px 18px rgba(0,0,0,0.85), 0 1px 4px rgba(0,0,0,0.6)',
								}
					}
				>
					{t('Welcome')}
				</h1>
				<p
					className={`mt-4 text-base sm:text-lg font-medium max-w-sm mx-auto ${
						isLight ? 'text-gray-600' : 'text-white'
					}`}
					style={
						isLight
							? undefined
							: {
									textShadow:
										'0 0 20px rgba(0,0,0,0.95), 0 0 8px rgba(0,0,0,0.85), 0 1px 3px rgba(0,0,0,0.7)',
								}
					}
				>
					{t(
						display.canBrowseCatalog
							? 'Sign in to browse products, place orders, and manage your account.'
							: 'Sign in to view your invoices, payments, and account activity.',
					)}
				</p>
				<button
					type="button"
					onClick={() => openLoginModal()}
					className="mt-10 inline-flex items-center justify-center gap-2 px-10 py-3.5 rounded-full bg-portal-accent hover:bg-portal-accent-hover text-white font-semibold text-base shadow-lg shadow-black/40 transition-colors min-h-touch"
				>
					<FontAwesomeIcon icon={faRightToBracket} className="text-sm" />
					{t('Sign In')}
				</button>

				{/* Secondary CTA for prospective customers who don't have a login
				    yet. Only shown when the storefront has self-serve registration
				    enabled. Navigates to the standalone /register route so it's a
				    real, deep-linkable URL. Outlined (vs the solid Sign In) to read
				    as the secondary action; theme-aware so it stays legible on both
				    the dark and light splash backdrops. */}
				{isRegistrationEnabled && (
					<div className="mt-4">
						<button
							type="button"
							onClick={() => navigate('/register')}
							className={`inline-flex items-center justify-center gap-2 px-10 py-3.5 rounded-full border font-semibold text-base transition-colors min-h-touch ${
								isLight
									? 'border-gray-300 text-[#0F1A2A] hover:bg-black/5'
									: 'border-white/40 text-white hover:bg-white/10'
							}`}
						>
							<FontAwesomeIcon icon={faUserPlus} className="text-sm" />
							{t('Create an account')}
						</button>
					</div>
				)}
			</div>
		</div>
	);
}
